/**
 * Design System - Theme Broadcast Channel
 *
 * Sincronización del tema ENTRE PESTAÑAS (única responsabilidad viva de este
 * módulo). El resto de la ex-fachada de persistencia (localStorage save/get/remove,
 * fetch/save al servidor, preferencia del sistema) fue BORRADA: no tenía
 * consumidores en runtime y `getSystemPreference`/`subscribeToSystemPreference`
 * reintroducían el modo `system` que DS-5 dropeó. La persistencia real la hace
 * next-themes (localStorage `sc-theme` + script anti-flash) y el `ThemeContext`
 * (server-side por usuario).
 */

import type { ThemeMode } from '../themes/types';

// =================================
// BROADCAST CHANNEL (sync tabs)
// =================================

/**
 * Nombre del canal de broadcast — ÚNICA fuente del literal (antes duplicado en
 * `THEME_CONFIG.broadcastChannel`, que era literal muerto).
 */
export const BROADCAST_CHANNEL_NAME = 'theme-sync';

interface ThemeBroadcastMessage {
  type: 'THEME_CHANGE';
  theme: ThemeMode;
  timestamp: number;
}

/**
 * Crear canal de broadcast para sincronizar tema entre pestañas
 */
export function createThemeBroadcastChannel(
  onThemeChange: (theme: ThemeMode) => void
): { broadcast: (theme: ThemeMode) => void; close: () => void } | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
    return null;
  }

  try {
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

    channel.onmessage = (event: MessageEvent<ThemeBroadcastMessage>) => {
      if (event.data.type === 'THEME_CHANGE') {
        onThemeChange(event.data.theme);
      }
    };

    return {
      broadcast: (theme: ThemeMode) => {
        const message: ThemeBroadcastMessage = {
          type: 'THEME_CHANGE',
          theme,
          timestamp: Date.now(),
        };
        channel.postMessage(message);
      },
      close: () => {
        channel.close();
      },
    };
  } catch {
    return null;
  }
}
